import { useGetActiveOrganization } from '@/features/organization/api';
import { queryKeys } from '@/lib/query-keys';
import { apiClient } from '@borradh-workspace/api-client';
import { publicSitePreviewSchema } from '@borradh-workspace/contracts';
import { useQuery } from '@tanstack/react-query';
import { useClaireDesigner } from './claire-designer-flag';
import { type SiteAppearance, appearancePreview } from './site-appearance';
import { previewLinks } from './site-preview-links';

export function SiteViewerPage({
  locationId,
  versionId,
  style,
  spacing,
  background,
  primary,
  accent,
}: SiteAppearance & {
  locationId: string;
  versionId: string;
  background?: string;
  primary?: string;
  accent?: string;
}) {
  const { enabled, loading } = useClaireDesigner();
  const { data: organization } = useGetActiveOrganization();
  const preview = useQuery({
    queryKey: queryKeys.publicSite.preview(
      organization?.id,
      locationId,
      versionId
    ),
    queryFn: () =>
      apiClient.get<{ html: string }>(
        `public-sites/${encodeURIComponent(locationId)}/versions/${encodeURIComponent(versionId)}`,
        { schema: publicSitePreviewSchema }
      ),
    enabled: enabled && !loading && Boolean(organization?.id),
    retry: false,
  });
  const html =
    enabled && !loading && preview.data?.html
      ? appearancePreview(preview.data.html, {
          style,
          spacing,
          ...(background && primary && accent
            ? { palette: { background, primary, accent } }
            : {}),
        })
      : null;
  return (
    <div className="fixed inset-0 z-50 bg-white">
      <title>Website preview | Claire Designer</title>
      {html ? (
        <iframe
          title="Website preview"
          srcDoc={previewLinks(html)}
          sandbox={`allow-popups allow-popups-to-escape-sandbox allow-top-navigation-by-user-activation${html.includes('<script id="claire-live-data"') ? ' allow-scripts' : ''}`}
          className="h-full w-full border-0"
        />
      ) : (
        <div className="flex h-full items-center justify-center text-sm text-muted-foreground">
          {loading || (enabled && !preview.isError)
            ? 'Loading preview…'
            : 'This website preview is unavailable.'}
        </div>
      )}
    </div>
  );
}
